import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    List,
    ListItem,
    Typography,
    Link,
    ListItemText,
    Divider,
} from '@mui/material';
import { Box } from '@mui/system';
import { useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { ADD_WORKING_HOUR_GROUP } from '../../../graphql/mutations';
import { EntityType, updateStaffWorkingHour, WorkingHourOperation } from '../../../redux/actions/businessActions';
import WorkingHourView from '../workingHourView';
import GenericTooltip from '../../common/genericToolTip';
import UpgradePlan from '../../../util/upgrade/upgradePlan';
import { getEffectivePlan } from '../../../util/utils';

const BookingTypeHostAvailability = ({
    allStaffs,
    providerUser,
    getSelectedGroupIdForStaff,
    bookingTypeCategoryName,
    groupId,
    setGroupId,
    isUpdateMode,
    selectedBookingType,
    draftGroupId,
    setDraftGroupId,
    getDefaultGroupId,
    custom,
    setCustom,
    selectedBusiness,
    assignedStaffs,
    setAssignedStaffs,
}) => {
    const isDisabled = false; // You can set this based on your logic
    const navigate = useNavigate(); // Get the navigate function from the hook
    const businessStaffs = selectedBusiness?.businessStaffs;
    const [currentStaffData, setCurrentStaffData] = useState(null);
    const [currentWorkingHours, setCurrentWorkingHours] = useState({
        workingHours: [],
        workingHourOverrides: [],
        workingHourHolidays: []
    });
    const [checkPlanNeeded, setCheckPlanNeeded] = useState(false);

    const [selects, setSelects] = useState(Number(groupId));
    const errorTimeoutRef = useRef(null);
    const errorTimeoutActiveRef = useRef(false);
    const errorRef = useRef(null);
    const dispatch = useDispatch();
    const currentStaff = businessStaffs?.find(
        (staff) => Number(staff.id) === providerUser.staffId
    );
    const standardPlanRequired = 2;

    const [self, setSelf] = useState(
        (selectedBookingType?.assignedStaffs?.length === 1 &&
            Number(selectedBookingType?.assignedStaffs[0].staffId) !==
            providerUser.staffId) ||
            selectedBookingType?.assignedStaffs?.length > 1
            ? false
            : true
    );

    useEffect(() => {
        const fetchData = () => {
            const data = getStaffData(currentStaff); 
            setCurrentStaffData(data);
        };

        if (currentStaff) {
            fetchData();
        }
    }, [currentStaff]);

    useEffect(() => { 
        if (currentStaffData) {
            const isCustom = isExistingWorkingHourGroupCustom(currentStaffData); 
            setCustom(isCustom);
        }
      }, [currentStaffData]);




    const [errorUpdating, setErrorUpdating] = useState(false);
    useEffect(() => {
        if (errorUpdating && !errorTimeoutActiveRef.current) {
            errorTimeoutActiveRef.current = true;

            errorTimeoutRef.current = setTimeout(() => {
                setErrorUpdating(false);
                errorTimeoutActiveRef.current = false;
            }, 2000);
        }

        return () => {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutActiveRef.current = false;
        };
    }, [errorUpdating]);
    useEffect(() => {
        if (errorRef.current) {
            errorRef.current.style.height = "0px";

            if (errorUpdating) {
                errorRef.current.style.transition = "height 0.3s ease";
                errorRef.current.style.height = "50px";
            } else {
                errorRef.current.style.height = "0px";
            }
        }
    }, [errorUpdating]);

    useEffect(() => {
        if (assignedStaffs.length > 0) {
            const updatedAssignedStaffs = assignedStaffs.map((staff) => {
                return { ...staff, groupId: groupId };
            });
            setAssignedStaffs(updatedAssignedStaffs);
        }
    }, [selectedBookingType, providerUser, groupId, custom]);

    useEffect(() => {

        updateCurrentWorkingHours();
    }, [currentStaffData, custom, isUpdateMode])

    const [addWorkingHourGroups] = useMutation(ADD_WORKING_HOUR_GROUP);

    const handleToggleStaff = (staff) => {
        const selectedIndex = assignedStaffs.findIndex((row) => row.staffId === Number(staff.id));
        const newSelected = [...assignedStaffs];
        const toggledStaffGroupId = custom ? groupId : getSelectedGroupIdForStaff(staff.id);

        if (selectedIndex === -1) {
            if (assignedStaffs.length === 0 || assignedStaffs.length === 1) {
                newSelected.push({ staffId: Number(staff.id), groupId: toggledStaffGroupId });
            } else {
                newSelected.push({ staffId: Number(staff.id), groupId: toggledStaffGroupId });
            }
        } else {
            if (assignedStaffs.length === 1) {
                return;
            } else {
                newSelected.splice(selectedIndex, 1);
            }
        }
        setAssignedStaffs(newSelected);
    };


    function getStaffData(targetStaff) {
        if (!targetStaff) {
            return null;
        }
        const workingHourGroups = targetStaff.workingHourGroups || [];
        const workingHourOverrideGroups =
            targetStaff.workingHourOverrideGroups || [];

        const nonCustomWHourGroups = workingHourGroups.filter(
            (workingGroup) => !workingGroup.custom
        );
        const customWHourGroups = workingHourGroups.filter(
            (workingGroup) => workingGroup.custom
        );
        const overrideData = workingHourOverrideGroups.workingHourOverrides || [];

        return {
            workingHourGroups,
            workingHourOverrideGroups,
            customWHourGroups,
            nonCustomWHourGroups,
            overrideData,
        };
    }

    function isExistingWorkingHourGroupCustom() {
        return (
            currentStaffData?.workingHourGroups?.find(
                (group) => Number(group.id) === groupId
            )?.custom
        );
    }

    const updateCurrentWorkingHours = () => {
        if (isUpdateMode && currentStaffData) {
            let currentWHourGroup = currentStaffData?.customWHourGroups.find(
                (group) =>
                    Number(group.id) === (custom ? draftGroupId ?? groupId : groupId)
            );
            const gId = Number(currentWHourGroup?.id);

            let workingHourOverrideGroup = currentStaffData?.workingHourOverrideGroups?.find(
                (group) =>
                    Number(group.groupId) === (custom ? draftGroupId ?? groupId : groupId)
            );

            setCurrentWorkingHours(prevState => ({
                ...prevState,
                workingHours: currentWHourGroup?.workingHours || [], // Update only workingHours array
                workingHourOverrides: workingHourOverrideGroup?.workingHourOverrides || []

            }));

            setSelects(gId);  // Keep existing functionality
        } else {
            // Handle cases where there is no match or data
            setCurrentWorkingHours(prevState => ({
                ...prevState,
                workingHours: []
            }));
        }
    };



    const handleProviderChange = (event) => {
        const selectedValue = event.target.value;
        const isSelf = selectedValue === "self";
        setSelf(isSelf);
        if (isSelf) {
            setAssignedStaffs([
                {
                    staffId: providerUser.staffId,
                    groupId: custom ? groupId : getSelectedGroupIdForStaff(providerUser.staffId),
                },
            ]);
            const gId =  custom ? groupId : getDefaultGroupId(providerUser.staffId);
            // setCustom(false);
            setGroupId(gId);
        } else {
              const effectivePlan = getEffectivePlan(providerUser) 

            if (effectivePlan < standardPlanRequired) {
                if (!checkPlanNeeded) {
                    setCheckPlanNeeded(true);
                }
                setSelf(true);
                return;
            }

            if (isUpdateMode) {
                const existingStaffs = selectedBookingType?.assignedStaffs?.map(
                    (assignedStaff) => ({
                        staffId: Number(assignedStaff.staffId),
                        groupId: custom ? groupId : Number(assignedStaff.groupId),
                    })
                );
                setAssignedStaffs(existingStaffs);
            } else {
                setAssignedStaffs([
                    {
                        staffId: providerUser.staffId,
                        groupId: custom ? groupId : getSelectedGroupIdForStaff(providerUser.staffId),
                    },
                ]);
            }
        }
    };



    const handleRadioChange = (event) => {
        const value = event.target.value;
        const isCustom = value === "custom";
        setCustom(isCustom);
        isCustom && !draftGroupId ? handleCreateCustomGroup() : updateCurrentWorkingHours();
        if (!isCustom) {
            const gId = getDefaultGroupId(providerUser.staffId);
            setCustom(false);
            setGroupId(gId);
        }
    };

    const handleCreateCustomGroup = () => {
        const staffIds = assignedStaffs.map((staff) => staff.staffId);
        const workingHourGroup = {
            staffIds: staffIds,
            businessId: providerUser.defaultBusinessId,
            custom: true,
            def: false,
            title: "Personal custom work hours",
            isForBusiness: 2,
            timeZoneId: currentStaffData?.nonCustomWHourGroups[0]?.timeZone?.id
        };
        addWorkingHourGroups({ variables: { input: workingHourGroup } })
            .then((res) => {
                let updatedWorkingHourHourGroupResponse = res.data?.addWorkingHourGroup;
                if (updatedWorkingHourHourGroupResponse && typeof updatedWorkingHourHourGroupResponse === 'object') {
                    // Destructure __typename and keep the rest of the properties
                    const { __typename, ...rest } = updatedWorkingHourHourGroupResponse;
                    updatedWorkingHourHourGroupResponse = rest;
                }

                const groupId = Number(updatedWorkingHourHourGroupResponse.workingHourGroup.id);
                setGroupId(groupId);
                setDraftGroupId(groupId);
                setCustom(true);
                // Dispatch updated working hours to the store 
                staffIds.forEach((staffId) => {
                    dispatch(
                        updateStaffWorkingHour(
                            providerUser.defaultBusinessId,
                            staffId,
                            updatedWorkingHourHourGroupResponse.workingHours,  // Pass each working hour as an array with a single element
                            updatedWorkingHourHourGroupResponse.workingHourGroup, // Complete group details when adding
                            WorkingHourOperation.ADD
                        )
                    );
                });
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const goToWorkingHours = () => {
        navigate("/biz/staff?tab=1");
    };

    const getCategorySpecificPrompt = (categoryName) => {
        switch (categoryName?.toLowerCase()) {
            case "event":
                return "Add a host for this event.";
            case "service":
                return "Who is going to provide this service?";
            case "class":
                return "Include an instructor for this class.";
            default:
                return "Invalid category name.";
        }
    };

    const tooltipData = {
        host: "You can choose to assign staff members to this booking type.",
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                marginLeft: 0,
                flexGrow: 1,         // Allow it to grow
                width: "100%",       // Ensure it uses full width of the parent
                height: "100%",      // Optionally, allow it to fill the height as well
            }}>

            {checkPlanNeeded && (
                <UpgradePlan
                    featureName={"Adding team members as a host"}
                    requiredPlanId={standardPlanRequired}
                    onClose={(skipUpgrade) => {
                        setCheckPlanNeeded(false);
                        if (skipUpgrade) {
                            // setOpenAddDialog(true);
                        }
                    }}
                />
            )}

            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <h4 style={{ margin: 0 }}>
                    {`${getCategorySpecificPrompt(bookingTypeCategoryName)}`}
                </h4>
                <GenericTooltip title={tooltipData.host} />
            </div>

            <RadioGroup aria-label="Provider" name="provider" value={self ? "self" : "team"} onChange={handleProviderChange}>
                <FormControlLabel value="self" control={<Radio />} label="Only myself" disabled={isDisabled} />
                <FormControlLabel value="team" control={<Radio />} label="Team members" disabled={isDisabled} />
            </RadioGroup>
            {!self && (
                <>
                    <Box sx={{ maxHeight: "400px", maxWidth: "300px", overflowY: "auto" }}>
                        <List>
                            {allStaffs?.map((staff) => (
                                <ListItem key={staff?.id} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Checkbox
                                        checked={assignedStaffs.some((row) => Number(row.staffId) === Number(staff?.id))}
                                        onChange={() => handleToggleStaff(staff)}
                                        color="primary"
                                        disabled={Number(staff.status) !== 1 || isDisabled || Number(selectedBookingType?.createdBy) === Number(staff?.id)}
                                    />
                                    <ListItemText primary={`${staff?.user?.username}` + (Number(staff.status) !== 1 ? " - pending" : Number(selectedBookingType?.createdBy) === Number(staff?.id) ? " [owner]" : "")} sx={{ flex: 1 }} />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                    {/* //TODO */}
                    {/* <Box sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                        <Typography>
                            <Checkbox type="checkbox" checked={allowUserChoice} onChange={handleToggleUserChoice} />
                            Let users select a provider from staff for the {`${bookingTypeCategoryName}`}
                        </Typography>
                    </Box> */}
                </>
            )}
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <Typography sx={{ margin: 0 }}>
                    {`Please choose which day of the week and what time your ${bookingTypeCategoryName} will take place.`}
                </Typography>

            </Box>
            <Box sx={{ display: "flex", flexDirection: "row" }}>
                <RadioGroup
                    aria-label="schedule"
                    name="schedule"
                    value={custom ? "custom" : "existing"}
                    onChange={handleRadioChange}
                    sx={{ display: "flex", flexDirection: "row" }}
                >
                    <FormControlLabel
                        disabled={isDisabled}
                        value="existing"
                        control={<Radio />}
                        label={
                            self ? (
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Typography component="span">Use your default </Typography>
                                    <Link
                                        sx={{
                                            textDecoration: "none",
                                            cursor: "pointer",
                                            marginLeft: 0.5,
                                        }}
                                        onClick={goToWorkingHours}
                                    >
                                        availability schedule
                                    </Link>
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Typography component="span">Use staff default </Typography>
                                    <Link
                                        sx={{
                                            textDecoration: "none",
                                            cursor: "pointer",
                                            marginLeft: 0.5,
                                        }}
                                        onClick={goToWorkingHours}
                                    >
                                        availability schedule
                                    </Link>
                                    <GenericTooltip title={tooltipData.visibility} />
                                </Box>
                            )
                        }
                    />
                    <FormControlLabel
                        disabled={isDisabled}
                        value="custom"
                        control={<Radio />}
                        label="Custom hours"
                    />
                </RadioGroup>
            </Box>
            <Divider sx={{ my: 2 }} />
            {custom && (
                <>
                    {currentWorkingHours?.workingHours?.length > 0 ? (
                        // <CustomAvailabilitySchedule
                        //     setErrorUpdating={setErrorUpdating}
                        //     groupId={groupId}
                        //     currentWorkingHours={currentWorkingHours}
                        //     providerUser={providerUser}
                        //     isDisabled={isDisabled}
                        // />
                        <WorkingHourView
                            key={Math.floor(Math.random() * 1000)}
                            providerUser={providerUser}
                            isDisabled={isDisabled}
                            entityType={EntityType.STAFF}
                            isCustom={true}
                            defaultWH={true}
                            hideOverride={true}
                            groupId={draftGroupId ?? groupId}
                        />
                    ) : (
                        <Typography>Creating....</Typography>
                    )}
                </>
            )}
            {
                errorUpdating && (
                    <div
                        ref={errorRef}
                        className="notification"
                        style={{ width: "170px", border: "2px solid red" }}
                    >
                        {errorUpdating}
                    </div>
                )
            }

        </Box>
    );
};

export default BookingTypeHostAvailability;
